/**
 * Toast region + provider. Used for upload errors and assessment
 * failures; lives outside the macOS window shell so toasts stay
 * legible when an action is still in flight.
 *
 * Repeats collapse and the stack is capped (ADR-0031). A single root
 * cause used to raise one toast per caller: the 2026-08-12 base-URL
 * incident put thirteen of them on screen, twelve identical, which
 * covered the workspace and buried the one message that differed.
 * The information an operator needs is "this failed, a lot", not
 * twelve copies of one sentence.
 *
 * The state transitions live in exported pure functions because the
 * Vitest suite runs under the `node` environment with no DOM, so the
 * component itself cannot be rendered in a unit test. Testing the
 * transitions is what actually pins the behaviour; the JSX below is
 * a thin projection of them.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

export interface Toast {
  id: number;
  tone: "ok" | "err";
  message: string;
  /** How many times this exact (tone, message) has been raised. */
  count: number;
  /** Absolute epoch-ms deadline, not a relative dwell. */
  expiresAt: number;
}

interface ToastApi {
  show(tone: Toast["tone"], message: string): void;
}

/** Most toasts on screen at once. Beyond this the oldest is dropped.
 *
 * With repeats collapsing, reaching this cap means four genuinely
 * different failures, which is already past the point where a stack
 * is readable. It is a backstop, not the primary control.
 */
export const MAX_VISIBLE = 4;

/** Errors dwell longer than successes: an upload failure often
 * surfaces over an open modal, and 4.5 s is not enough for an
 * operator to read a sentence-long failure reason before it
 * vanishes. Errors are also click-to-dismiss so a user who has read
 * one can clear it manually.
 */
export const DWELL_MS: Record<Toast["tone"], number> = {
  err: 12000,
  ok: 4500,
};

/**
 * Fold a newly raised toast into the visible stack.
 *
 * An identical (tone, message) pair increments the existing entry's
 * count and pushes its deadline out, rather than appending a
 * duplicate. The entry keeps its position so a stack that is being
 * hammered does not reorder itself under the operator's eyes.
 *
 * @param prev The current stack.
 * @param incoming The toast being raised; `id` is used only when it
 *   turns out to be new.
 * @param maxVisible Stack ceiling. Defaults to {@link MAX_VISIBLE}.
 * @returns The next stack. Oldest entries are dropped first when the
 *   ceiling is exceeded.
 *
 * @example
 * // Twelve identical failures collapse to one entry counting twelve.
 * applyToast(applyToast([], a, 1), { ...a, id: 2 }, 2)
 * // => [{ ...a, count: 2 }]
 */
export function applyToast(
  prev: Toast[],
  incoming: Toast,
  maxVisible: number = MAX_VISIBLE,
): Toast[] {
  const index = prev.findIndex(
    (t) => t.tone === incoming.tone && t.message === incoming.message,
  );
  if (index !== -1) {
    const next = [...prev];
    const existing = next[index]!;
    next[index] = {
      ...existing,
      count: existing.count + 1,
      expiresAt: incoming.expiresAt,
    };
    return next;
  }
  const appended = [...prev, incoming];
  return appended.length > maxVisible
    ? appended.slice(appended.length - maxVisible)
    : appended;
}

/**
 * Drop every toast whose deadline has passed.
 *
 * Returns the original array reference when nothing expired. That
 * identity matters: the prune runs from an effect keyed on the
 * stack, so returning a fresh-but-equal array would re-trigger the
 * effect and spin.
 *
 * @param prev The current stack.
 * @param now Epoch milliseconds to judge against.
 * @returns The surviving toasts, or `prev` unchanged when none expired.
 */
export function pruneExpired(prev: Toast[], now: number): Toast[] {
  const next = prev.filter((t) => t.expiresAt > now);
  return next.length === prev.length ? prev : next;
}

const ToastCtx = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const show = useCallback((tone: Toast["tone"], message: string) => {
    const id = nextId.current;
    nextId.current += 1;
    setToasts((prev) =>
      applyToast(prev, {
        id,
        tone,
        message,
        count: 1,
        expiresAt: Date.now() + DWELL_MS[tone],
      }),
    );
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    if (toasts.length === 0) return;
    // Wake at the soonest absolute deadline. The previous version
    // set its timer from the head of the stack and re-ran on every
    // change, so a steady stream of new toasts cleared and restarted
    // that timer indefinitely and the head never expired. Deadlines
    // are absolute here, so a restart recomputes the same instant
    // and the stack still drains under load.
    const soonest = Math.min(...toasts.map((t) => t.expiresAt));
    const timer = setTimeout(
      () => setToasts((prev) => pruneExpired(prev, Date.now())),
      Math.max(0, soonest - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [toasts]);

  return (
    <ToastCtx.Provider value={{ show }}>
      {children}
      <div className="toast-region" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`toast ${t.tone}`}
            onClick={() => dismiss(t.id)}
            role={t.tone === "err" ? "button" : undefined}
            title={t.tone === "err" ? "Click to dismiss" : undefined}
          >
            <span>{t.message}</span>
            {t.count > 1 && (
              <span className="toast-count" aria-label={`${t.count} occurrences`}>
                &times;{t.count}
              </span>
            )}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

/**
 * Hook for raising a toast.
 *
 * Components call ``useToast().show("err", msg)`` on a transport or
 * domain failure; the provider auto-dismisses after a few seconds.
 * Raising the same message repeatedly is safe: repeats collapse into
 * a counted entry rather than stacking.
 */
export function useToast(): ToastApi {
  const api = useContext(ToastCtx);
  if (!api) {
    throw new Error("useToast called outside ToastProvider");
  }
  return api;
}
